import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalService } from '../../shared-services/global.service';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-scatter-chart',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './scatter-chart.component.html',
  styleUrls: ['./scatter-chart.component.scss']
})
export class ScatterChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  private myChart!: echarts.ECharts;
  private destroy$ = new Subject<void>();
  private globalFilters: any = {};
  isWaitingForScenarios = true;
  

  constructor(
    private configService: ConfigService,
    private globalService: GlobalService
  ) {}



ngAfterViewInit(): void {
  // Scenario subscription
  this.globalService.scenarioGeneration$
    .pipe(takeUntil(this.destroy$))
    .subscribe(scenarioPayload => {
      this.isWaitingForScenarios = false; // hide initial message
      const currentGlobalFilters = this.globalService.getCurrentFilters().filter;
      const combinedFilters = { ...currentGlobalFilters, ...scenarioPayload };
      this.loadAndRenderChart(combinedFilters, true);
    });

  if (!this.data.ignoreGlobalFilters) {
    this.globalService.filters$
      .pipe(takeUntil(this.destroy$))
      .subscribe(filters => {
        if (filters && Object.keys(filters.filter || {}).length > 0) {
          this.isWaitingForScenarios = false; // hide initial message
          this.globalFilters = filters.filter;
          this.loadAndRenderChart(this.globalFilters, false);
        } else {
          this.isWaitingForScenarios = true; // show message
        }
      });
  }
}

private loadAndRenderChart(filters?: any, isScenario = false): void {
  const chartDom = document.getElementById(this.data.widgetId);
  if (!chartDom) return;

  if (!this.myChart) {
    this.myChart = echarts.init(chartDom);
  }

  // Show ECharts loading spinner
  const loadingText = isScenario ? 'Simulating data...' : 'Loading...';
  this.myChart.showLoading('default', {
    text: loadingText,
    color: '#198ed2ff',
    textColor: '#198ed2ff',
    maskColor: 'rgba(255, 255, 255, 0.7)',
    fontSize: 16,
    showSpinner: true,
    spinnerRadius: 10
  });

  // Hide the placeholder text once API call starts
  this.isWaitingForScenarios = false;

  this.configService.getConfig(this.data.apiUrl, filters).subscribe({
    next: (res) => {
      this.myChart.hideLoading();

      const pointsData = res?.data?.points;
      if (!pointsData) return;

      const actualPoints = pointsData.actual ? [pointsData.actual] : [];
      const normalPoints = pointsData.scenerios || [];
      this.renderChart({ points: normalPoints, actualPoints });

      this.addResizeListener();
    },
    error: () => {
      this.myChart.hideLoading();
    }
  });
}




 private renderChart(chartData?: { points: any[]; actualPoints?: any[] }): void {
  if (!this.data) return;

  const highlightActual = this.data.highlightActual === true;
  const normalPoints = chartData?.points || [];
  const actualPoints = highlightActual ? chartData?.actualPoints || [] : [];

  console.log('Rendering scatter chart with data:', normalPoints, actualPoints);

  const series: echarts.SeriesOption[] = [
    {
      type: 'scatter',
      symbolSize: 12,
      data: normalPoints,
      itemStyle: { color: '#91cc75' }
    }
  ];

  if (highlightActual && actualPoints.length > 0) {
    series.push({
      type: 'scatter',
      symbolSize: 14,
      data: actualPoints,
      itemStyle: { color: '#d9534f' },
      emphasis: { itemStyle: { borderColor: '#000', borderWidth: 2 } }
    });
  }

  const option: echarts.EChartsOption = {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const value = params.value || [];
        return `X: ${value[0] || ''} <br>Y: ${value[1] || ''} <br>${value[2] || ''}`;
      }
    },
    xAxis: { type: 'value' },
    yAxis: { type: 'value' },
    series
  };

  this.myChart.setOption(option);
  this.myChart.hideLoading();
}



  private addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
